import * as React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { ArrowLeft, ShieldCheck, ArrowUpRight, Copy } from 'lucide-react';
import { useAuth, type Identity } from './AuthProvider';
import { MfaChallengeStep } from './MfaChallengeStep';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api, ApiError } from '../api-client';
import { WorqlyMark } from '../../design-system/components/WorqlyMark';
import { appUrl, getHostMode } from '../hostMode';
import { TurnstileWidget } from '../../marketing/components/TurnstileWidget';

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return String((err as any)?.message ?? err);
}

/**
 * Enrollment flow for an admin blocked by `mfa_enrollment_required` — the
 * platform requires operator 2FA and this admin hasn't set it up yet. Runs
 * on the short-lived `enroll_token` (no session exists yet): enroll → scan
 * QR / enter key → confirm → show recovery codes once → prompt to sign in
 * again (confirming never issues a session, mirroring the enroll-token
 * contract in api/core/auth/mfa.routes.ts).
 */
type AdminEnrollStep = 'loading' | 'setup' | 'codes' | 'error';

function AdminMfaEnrollmentStep({
  enrollToken,
  onLoginAgain,
}: {
  enrollToken: string;
  onLoginAgain: () => void;
}) {
  const [step, setStep] = React.useState<AdminEnrollStep>('loading');
  const [otpauthUrl, setOtpauthUrl] = React.useState('');
  const [manualKey, setManualKey] = React.useState('');
  const [qrDataUrl, setQrDataUrl] = React.useState('');
  const [code, setCode] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[]>([]);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ otpauth_url: string; manual_key: string }>(
          '/api/auth/mfa/enroll',
          { method: 'POST', body: { enroll_token: enrollToken }, noRedirectOn401: true },
        );
        if (cancelled) return;
        setOtpauthUrl(r.otpauth_url);
        setManualKey(r.manual_key);
        const qr = await QRCode.toDataURL(r.otpauth_url);
        if (cancelled) return;
        setQrDataUrl(qr);
        setStep('setup');
      } catch (err) {
        if (cancelled) return;
        setError(errMessage(err));
        setStep('error');
      }
    })();
    return () => { cancelled = true; };
  }, [enrollToken]);

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ recovery_codes: string[] }>(
        '/api/auth/mfa/confirm',
        { method: 'POST', body: { enroll_token: enrollToken, code: code.trim() }, noRedirectOn401: true },
      );
      setRecoveryCodes(r.recovery_codes);
      setStep('codes');
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function copyAll() {
    navigator.clipboard?.writeText(recoveryCodes.join('\n')).then(
      () => { setCopied(true); },
      () => { /* clipboard unavailable — codes stay visible for manual copy */ },
    );
  }

  if (step === 'loading') {
    return <div className="py-8 text-center text-[13.5px] text-ink/60 dark:text-white/55">Setting up two-factor authentication…</div>;
  }

  if (step === 'error') {
    return (
      <div className="space-y-4">
        <p className="text-[13.5px] font-semibold text-ink/80 dark:text-white/80">Couldn't start enrollment</p>
        <p className="text-[13px] text-rose-600 dark:text-rose-300">{error}</p>
        <Button variant="outline" onClick={onLoginAgain} className="w-full">Back to sign in</Button>
      </div>
    );
  }

  if (step === 'setup') {
    return (
      <form onSubmit={onConfirm} className="space-y-4">
        <div>
          <p className="text-[13.5px] font-semibold text-ink/80 dark:text-white/80">
            Set up two-factor authentication
          </p>
          <p className="text-[12.5px] text-ink/55 dark:text-white/55 mt-1">
            Operator accounts require two-factor authentication. Scan this QR code with your
            authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit
            code it shows.
          </p>
        </div>

        {qrDataUrl && (
          <div className="flex justify-center">
            <img src={qrDataUrl} alt="Scan with your authenticator app" width={200} height={200} className="rounded-xl ring-1 ring-ink/10 dark:ring-white/15" />
          </div>
        )}

        <div>
          <Label className="text-[11.5px] text-ink/55 dark:text-white/50">Can't scan? Enter this key manually</Label>
          <p className="mt-1 font-mono text-[13px] break-all bg-ink/5 dark:bg-white/5 rounded-lg px-3 py-2 text-ink dark:text-white">
            {manualKey}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="admin-enroll-code">6-digit code</Label>
          <Input
            id="admin-enroll-code"
            autoFocus
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="tracking-[0.2em] text-center font-mono"
          />
        </div>

        {error && (
          <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
            {error}
          </div>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={submitting || !code.trim()}>
          {submitting ? 'Verifying…' : 'Verify & continue'}
        </Button>
      </form>
    );
  }

  // step === 'codes'
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[13.5px] font-semibold text-ink/80 dark:text-white/80">Save your recovery codes</p>
        <p className="text-[12.5px] text-ink/55 dark:text-white/55 mt-1">
          Store these now — shown once. Each code signs you in one time if you lose access to your
          authenticator app.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-2xl bg-ink/5 dark:bg-white/5 p-4 font-mono text-[13.5px] tabular-nums">
        {recoveryCodes.map((c) => (
          <div key={c} className="text-ink dark:text-white">{c}</div>
        ))}
      </div>

      <Button type="button" variant="secondary" size="sm" onClick={copyAll} className="w-full">
        <Copy className="h-3.5 w-3.5" />
        {copied ? 'Copied' : 'Copy all'}
      </Button>

      <Button type="button" size="lg" className="w-full" onClick={onLoginAgain}>
        Continue to sign in
      </Button>
    </div>
  );
}

export function AdminLoginPage() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState('');
  const [widgetEpoch, setWidgetEpoch] = React.useState(0);
  const [mfaToken, setMfaToken] = React.useState<string | null>(null);
  const [enrollToken, setEnrollToken] = React.useState<string | null>(null);
  const [justEnrolled, setJustEnrolled] = React.useState(false);

  function goNext() {
    const next = (location.state as any)?.from ?? '/admin';
    if (getHostMode() === 'marketing') {
      window.location.replace(appUrl(next));
    } else {
      navigate(next, { replace: true });
    }
  }

  function onMfaSuccess(identity: Identity) {
    if (identity.type !== 'super_admin') {
      logout({ redirectTo: null });
      setError('This account is not a Worqly operator. Use the customer sign-in at /login.');
      setMfaToken(null);
      return;
    }
    goNext();
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const me = await login({ email, password, turnstileToken, type: 'super_admin' });
      if ('mfa_required' in me) {
        setMfaToken(me.mfa_token);
        return;
      }
      if ('mfa_enrollment_required' in me) {
        setEnrollToken(me.enroll_token);
        return;
      }
      if ('workspace_selection_required' in me) {
        // Unreachable: the workspace picker is a tenant-user concept and this
        // page always sends type='super_admin'. Defensive narrow only.
        setError('This account is not a Worqly operator. Use the customer sign-in at /login.');
        return;
      }
      if (me.type !== 'super_admin') {
        logout({ redirectTo: null });
        setError('This account is not a Worqly operator. Use the customer sign-in at /login.');
        return;
      }
      goNext();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'captcha_failed') {
          setError(`Captcha: ${err.message || 'verification failed'} — please try again.`);
          setTurnstileToken('');
          setWidgetEpoch((n) => n + 1);
        } else if (err.code === 'rate_limited') {
          setError('Too many login attempts from your IP — please wait a few minutes and try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError(String((err as any)?.message ?? err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen flex flex-col">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-pds-mesh opacity-95" />

      <header className="relative z-10 px-6 lg:px-10 h-16 flex items-center justify-between">
        <span className="inline-flex items-center gap-2" aria-label="Worqly operator portal">
          <WorqlyMark size="md" />
          <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-ink text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] font-bold dark:bg-white/10 dark:text-white/80">
            <ShieldCheck className="h-3 w-3" />
            Operator
          </span>
        </span>
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/65 hover:text-ink dark:text-white/70 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Customer sign-in
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-coral font-bold mb-3">Operator portal</p>
            <h1 className="font-light text-display-md text-ink dark:text-white tracking-[-0.025em] leading-[1.05]">Worqly admin.</h1>
            <p className="mt-3 text-[13px] text-ink/60 dark:text-white/60">Restricted access. Activity is logged.</p>
          </div>

          <div className="rounded-3xl bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card p-7">
            {mfaToken ? (
              <MfaChallengeStep
                mfaToken={mfaToken}
                onBack={() => { setMfaToken(null); setError(null); }}
                onSuccess={onMfaSuccess}
              />
            ) : enrollToken ? (
              <AdminMfaEnrollmentStep
                enrollToken={enrollToken}
                onLoginAgain={() => { setEnrollToken(null); setPassword(''); setJustEnrolled(true); }}
              />
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {justEnrolled && (
                  <div className="text-[13px] text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-500/20 rounded-2xl px-3 py-2" role="status">
                    Two-factor authentication enabled. Please sign in again.
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[12px] font-semibold text-ink/70 dark:text-white/70">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    placeholder="operator@worqly.io"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-[12px] font-semibold text-ink/70 dark:text-white/70">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <TurnstileWidget
                  key={widgetEpoch}
                  onToken={setTurnstileToken}
                  onExpire={() => setTurnstileToken('')}
                />
                {error && (
                  <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
                    {error}
                  </div>
                )}
                <Button type="submit" size="lg" className="w-full" disabled={submitting || !turnstileToken}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            )}
          </div>

          {/* There is deliberately no "Forgot password" here: admin resets are
              restricted to the SUPER_ADMIN_RESET_EMAILS allowlist, so the link
              would silently do nothing for most admins. Say so, rather than
              leaving someone retyping a password they were never given — an
              invited admin has no password at all until they use their invite
              link, and that is exactly the confusion this page produced. */}
          <p className="text-center text-[12.5px] text-ink/50 dark:text-white/50 mt-6">
            Can't get in? Admin accounts are set up by invitation — your invite link is what
            creates your password, and it can only be used once. If it has expired, ask the
            platform owner to send you a new invite. Password reset by email is not available
            for admin accounts.
          </p>

          <p className="text-center text-[13px] text-ink/60 dark:text-white/60 mt-8">
            Customer?{' '}
            <Link to="/login" className="inline-flex items-center gap-1 font-bold text-ink dark:text-white hover:text-coral transition-colors">
              Use the customer sign-in
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
